import { useEffect, useRef, useState } from "react";
import { Html5Qrcode, Html5QrcodeSupportedFormats } from "html5-qrcode";
import { toast } from "sonner";
import { Camera, CheckCircle2, Download, Keyboard, LogIn, LogOut, Pencil, Plus, RefreshCcw, Users } from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import DataTable from "../components/common/DataTable";
import StatCard from "../components/common/StatCard";
import { erpApi, formatDate } from "../services/erpApi";
import { useAuth } from "../contexts/AuthContext";

const READER_ID = "attendance-camera-reader";
const emptyCorrection = { employee_id: "", attendance_date: "", login_time: "", logout_time: "", attendance_status: "present", correction_reason: "", remarks: "" };

const timeText = (value) => value ? new Date(value).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" }) : "-";
const looksLikeEmployeeQr = (value) => {
  const raw = String(value || "").trim();
  if (!raw) return false;
  if (raw.startsWith("PCQR:")) return raw.split(":").length >= 3;
  if (/^EMP[-_]/i.test(raw)) return true;
  try {
    const payload = JSON.parse(raw);
    return Boolean(payload.employee_id || payload.employee_uid || payload.qr_code_id || payload.qr_token);
  } catch {
    return false;
  }
};

const attendanceErrorMessage = (error) => {
  if (!error.response) {
    return "Server/CORS connection failed. Render backend deploy aur allowed domain check karein.";
  }
  if (error.response.status === 403) return error.response.data?.detail || "Yeh QR kisi dusre clinic ka hai.";
  if (error.response.status === 404) return error.response.data?.detail || "Employee QR record nahi mila.";
  return error.response.data?.detail || "Invalid employee QR code";
};

const canManageManualAttendance = (user) =>
  ["clinic_admin", "admin"].includes(user?.role) &&
  String(user?.name || "").trim().replace(/\s+/g, " ").toLowerCase() === "avinash pokharna";

export default function Attendance() {
  const { user } = useAuth();
  const scannerRef = useRef(null);
  const lastScanRef = useRef({ value: "", at: 0 });
  const [logs, setLogs] = useState([]);
  const [today, setToday] = useState({});
  const [qr, setQr] = useState("");
  const [cameraActive, setCameraActive] = useState(false);
  const [scanBusy, setScanBusy] = useState(false);
  const [scanMode, setScanMode] = useState("auto");
  const [cameraError, setCameraError] = useState("");
  const [lastResult, setLastResult] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [correctionOpen, setCorrectionOpen] = useState(false);
  const [editingLog, setEditingLog] = useState(null);
  const [correctionForm, setCorrectionForm] = useState(emptyCorrection);
  const canManageManual = canManageManualAttendance(user);

  const load = async () => {
    const [todayRes, historyRes, employeesRes] = await Promise.all([erpApi.attendanceToday(), erpApi.attendanceHistory(), erpApi.employees({ active: true })]);
    setToday(todayRes.data);
    setLogs(historyRes.data);
    setEmployees(employeesRes.data);
  };

  useEffect(() => {
    load().catch(() => toast.error("Failed to load attendance"));
    return () => stopCamera();
  }, []);

  const captureSnapshot = () => {
    const video = document.querySelector(`#${READER_ID} video`);
    if (!video || !video.videoWidth) return "";
    const canvas = document.createElement("canvas");
    canvas.width = 320;
    canvas.height = Math.round((video.videoHeight / video.videoWidth) * 320);
    const context = canvas.getContext("2d");
    context.drawImage(video, 0, 0, canvas.width, canvas.height);
    return canvas.toDataURL("image/jpeg", 0.45);
  };

  const submitScan = async (decodedText, source = "camera", actionOverride = "") => {
    const value = String(decodedText || "").trim();
    if (!value || scanBusy) return;
    if (!looksLikeEmployeeQr(value)) {
      toast.error("Employee QR card scan karein. Yeh ParshCare attendance QR nahi hai.");
      return;
    }
    const now = Date.now();
    if (lastScanRef.current.value === value && now - lastScanRef.current.at < 5000) return;
    lastScanRef.current = { value, at: now };
    setScanBusy(true);
    try {
      const res = await erpApi.scanAttendance({
        qr_code_id: value,
        action: actionOverride || scanMode,
        scan_device: `${source} | ${navigator.userAgent}`,
        scan_image_optional: source === "camera" ? captureSnapshot() : "",
      });
      setLastResult(res.data);
      const action = res.data.action === "logout" ? "logout" : res.data.action === "completed" ? "already completed" : "login";
      toast.success(`${res.data.employee?.name || "Employee"} ${action} recorded`);
      setQr("");
      load();
    } catch (error) {
      toast.error(attendanceErrorMessage(error));
    } finally {
      setScanBusy(false);
    }
  };

  const startCamera = async () => {
    setCameraError("");
    if (cameraActive) return;
    try {
      const scanner = new Html5Qrcode(READER_ID, { formatsToSupport: [Html5QrcodeSupportedFormats.QR_CODE], verbose: false });
      scannerRef.current = scanner;
      await scanner.start(
        { facingMode: "environment" },
        { fps: 10, qrbox: (width, height) => {
          const size = Math.min(width, height) * 0.72;
          return { width: size, height: size };
        } },
        (decodedText) => submitScan(decodedText, "camera"),
        () => {}
      );
      setCameraActive(true);
    } catch (error) {
      setCameraError("Camera start nahi hua. Browser permission allow karein ya manual QR box use karein.");
      toast.error("Camera permission needed");
    }
  };

  const stopCamera = async () => {
    if (!scannerRef.current) return;
    try {
      await scannerRef.current.stop();
      await scannerRef.current.clear();
    } catch {
      // Scanner may already be stopped during route changes.
    } finally {
      scannerRef.current = null;
      setCameraActive(false);
    }
  };

  const exportCsv = async () => {
    const response = await erpApi.attendanceExport();
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `attendance_${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  const localDateTimeValue = (value) => value ? new Date(value).toISOString().slice(0, 16) : "";
  const toIso = (value) => value ? new Date(value).toISOString() : "";

  const openManual = () => {
    setEditingLog(null);
    setCorrectionForm({ ...emptyCorrection, attendance_date: new Date().toISOString().slice(0, 10) });
    setCorrectionOpen(true);
  };

  const openCorrection = (log) => {
    setEditingLog(log);
    setCorrectionForm({
      employee_id: log.employee_id || "",
      attendance_date: log.attendance_date || "",
      login_time: localDateTimeValue(log.login_time),
      logout_time: localDateTimeValue(log.logout_time),
      attendance_status: log.attendance_status || "present",
      correction_reason: log.correction_reason || "",
      remarks: log.remarks || "",
    });
    setCorrectionOpen(true);
  };

  const submitCorrection = async (event) => {
    event.preventDefault();
    if (!correctionForm.employee_id || !correctionForm.attendance_date || !correctionForm.correction_reason) {
      toast.error("Employee, date and correction reason are required");
      return;
    }
    const payload = {
      ...correctionForm,
      login_time: toIso(correctionForm.login_time),
      logout_time: toIso(correctionForm.logout_time),
    };
    try {
      if (editingLog) {
        await erpApi.updateAttendance(editingLog.id, payload);
        toast.success("Attendance corrected");
      } else {
        await erpApi.createManualAttendance(payload);
        toast.success("Manual attendance added");
      }
      setCorrectionOpen(false);
      setEditingLog(null);
      setCorrectionForm(emptyCorrection);
      load();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save attendance");
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="font-manrope text-2xl font-bold">Smart QR Attendance</h1>
          <p className="text-sm text-hospital-text-muted">Camera se employee QR scan karein. System login/logout automatically record karega.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {canManageManual && <Button variant="outline" onClick={openManual}><Plus className="mr-2 h-4 w-4" />Manual Entry</Button>}
          <Button variant="outline" onClick={exportCsv}><Download className="mr-2 h-4 w-4" />Export</Button>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
        <StatCard title="Active Staff" value={today.active_employees || 0} icon={Users} />
        <StatCard title="Present Today" value={today.present || 0} icon={CheckCircle2} tone="success" />
        <StatCard title="Logged Out" value={today.logged_out || 0} icon={LogOut} />
        <StatCard title="Absent Estimate" value={today.absent_estimate || 0} icon={LogIn} tone="warning" />
      </div>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-[1.05fr_0.95fr]">
        <Card className="border border-gray-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Camera className="h-4 w-4 text-hospital-primary" />
              Camera Scanner
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="overflow-hidden rounded-xl border border-gray-200 bg-slate-950">
              <div id={READER_ID} className="min-h-[340px] w-full text-white" />
            </div>
            {cameraError && <div className="rounded-lg bg-red-50 p-3 text-sm text-red-700">{cameraError}</div>}
            {scanBusy && <div className="rounded-lg bg-hospital-primary-light p-3 text-sm text-hospital-primary">QR mila, attendance save ho rahi hai...</div>}
            <div className="grid grid-cols-3 gap-2 rounded-lg bg-gray-50 p-2">
              {[
                ["auto", "Auto"],
                ["login", "Login"],
                ["logout", "Logout"],
              ].map(([value, label]) => (
                <Button key={value} type="button" variant={scanMode === value ? "default" : "outline"} className={scanMode === value ? "btn-primary" : ""} onClick={() => setScanMode(value)}>
                  {label}
                </Button>
              ))}
            </div>
            <p className="text-sm text-hospital-text-muted">
              {scanMode === "logout" ? "Logout mode: employee QR scan karte hi logout save hoga." : scanMode === "login" ? "Login mode: employee QR scan karte hi login save hoga." : "Auto mode: pehla scan login, agla scan logout."}
            </p>
            <div className="flex flex-wrap gap-2">
              <Button className="btn-primary" onClick={startCamera} disabled={cameraActive}>
                <Camera className="mr-2 h-4 w-4" />
                Start Camera
              </Button>
              <Button variant="outline" onClick={stopCamera} disabled={!cameraActive}>Stop Camera</Button>
              <Button variant="outline" onClick={load}><RefreshCcw className="mr-2 h-4 w-4" />Refresh</Button>
            </div>
          </CardContent>
        </Card>

        <div className="space-y-6">
          <Card className="border border-gray-200">
            <CardHeader><CardTitle className="text-base">Last Scan</CardTitle></CardHeader>
            <CardContent>
              {lastResult ? (
                <div className="space-y-3">
                  <div className="flex items-center gap-3 rounded-lg bg-emerald-50 p-4 text-emerald-800">
                    <CheckCircle2 className="h-6 w-6" />
                    <div>
                      <p className="font-semibold">{lastResult.employee?.name}</p>
                      <p className="text-sm capitalize">{lastResult.action} recorded</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div className="rounded-lg bg-gray-50 p-3"><p className="text-hospital-text-muted">Login</p><p className="font-semibold">{timeText(lastResult.login_time)}</p></div>
                    <div className="rounded-lg bg-gray-50 p-3"><p className="text-hospital-text-muted">Logout</p><p className="font-semibold">{timeText(lastResult.logout_time)}</p></div>
                    <div className="rounded-lg bg-gray-50 p-3"><p className="text-hospital-text-muted">Hours</p><p className="font-semibold">{lastResult.total_hours || lastResult.work_hours || 0}</p></div>
                    <div className="rounded-lg bg-gray-50 p-3"><p className="text-hospital-text-muted">Status</p><p className="font-semibold capitalize">{lastResult.attendance_status}</p></div>
                  </div>
                </div>
              ) : (
                <p className="rounded-lg border border-dashed border-gray-300 bg-gray-50 p-6 text-sm text-hospital-text-muted">Camera start karke employee QR card scan karein.</p>
              )}
            </CardContent>
          </Card>

          <Card className="border border-gray-200">
            <CardHeader><CardTitle className="flex items-center gap-2 text-base"><Keyboard className="h-4 w-4" />Manual Backup</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <div className="space-y-2">
                <Label>QR text paste karein</Label>
                <Input value={qr} onChange={(e) => setQr(e.target.value)} placeholder="PCQR:employee_id:secure_token" />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Button className="btn-primary" disabled={!qr || scanBusy} onClick={() => { setScanMode("login"); submitScan(qr, "manual", "login"); }}>Login</Button>
                <Button variant="outline" disabled={!qr || scanBusy} onClick={() => { setScanMode("logout"); submitScan(qr, "manual", "logout"); }}>Logout</Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      <DataTable
        title="Attendance History"
        rows={logs}
        empty="No attendance logs"
        columns={[
          { key: "employee_name", label: "Employee" },
          { key: "attendance_date", label: "Date", render: (row) => formatDate(row.attendance_date) },
          { key: "login_time", label: "Login", render: (row) => timeText(row.login_time) },
          { key: "logout_time", label: "Logout", render: (row) => timeText(row.logout_time) },
          { key: "scheduled_start", label: "Shift", render: (row) => row.scheduled_start ? `${row.scheduled_start}-${row.scheduled_end}` : "-" },
          { key: "total_hours", label: "Hours", render: (row) => row.total_hours || row.work_hours || 0 },
          { key: "attendance_status", label: "Status", render: (row) => <span className="capitalize">{row.attendance_status || "present"}</span> },
          { key: "late_entry", label: "Late", render: (row) => row.late_entry ? "Yes" : "No" },
          { key: "half_day", label: "Half Day", render: (row) => row.half_day ? "Yes" : "No" },
          { key: "manual_override", label: "Edited", render: (row) => row.manual_override ? "Yes" : "No" },
          ...(canManageManual ? [{ key: "actions", label: "Action", render: (row) => <Button variant="ghost" size="icon" title="Correct attendance" onClick={() => openCorrection(row)}><Pencil className="h-4 w-4 text-hospital-primary" /></Button> }] : []),
        ]}
      />
      <Dialog open={correctionOpen} onOpenChange={setCorrectionOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>{editingLog ? "Correct Attendance" : "Manual Attendance Entry"}</DialogTitle></DialogHeader>
          <form onSubmit={submitCorrection} className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="space-y-2 sm:col-span-2">
              <Label>Employee</Label>
              <Select value={correctionForm.employee_id} onValueChange={(value) => setCorrectionForm({ ...correctionForm, employee_id: value })}>
                <SelectTrigger><SelectValue placeholder="Select employee" /></SelectTrigger>
                <SelectContent>{employees.map((employee) => <SelectItem key={employee.id} value={employee.id}>{employee.name} - {employee.role}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2"><Label>Date</Label><Input type="date" value={correctionForm.attendance_date} onChange={(e) => setCorrectionForm({ ...correctionForm, attendance_date: e.target.value })} /></div>
            <div className="space-y-2"><Label>Status</Label><Select value={correctionForm.attendance_status} onValueChange={(value) => setCorrectionForm({ ...correctionForm, attendance_status: value })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="present">Present</SelectItem><SelectItem value="late">Late</SelectItem><SelectItem value="half_day">Half Day</SelectItem><SelectItem value="absent">Absent</SelectItem></SelectContent></Select></div>
            <div className="space-y-2"><Label>Login Time</Label><Input type="datetime-local" value={correctionForm.login_time} onChange={(e) => setCorrectionForm({ ...correctionForm, login_time: e.target.value })} /></div>
            <div className="space-y-2"><Label>Logout Time</Label><Input type="datetime-local" value={correctionForm.logout_time} onChange={(e) => setCorrectionForm({ ...correctionForm, logout_time: e.target.value })} /></div>
            <div className="space-y-2 sm:col-span-2"><Label>Correction Reason</Label><Input value={correctionForm.correction_reason} onChange={(e) => setCorrectionForm({ ...correctionForm, correction_reason: e.target.value })} placeholder="Example: forgot QR scan, wrong logout time" /></div>
            <div className="space-y-2 sm:col-span-2"><Label>Remarks</Label><Input value={correctionForm.remarks} onChange={(e) => setCorrectionForm({ ...correctionForm, remarks: e.target.value })} /></div>
            <div className="flex justify-end gap-2 sm:col-span-2"><Button type="button" variant="outline" onClick={() => setCorrectionOpen(false)}>Cancel</Button><Button className="btn-primary">Save Attendance</Button></div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
